import React, {useState} from 'react'
import {Block, Col, Row} from 'jsxstyle'
import {CSS} from '@dnd-kit/utilities'
import {XpertKnowledgeSubSectionDto} from '../../../../services/XpertKnowledgeSection/type'
import DynamicFormField from '../../../../components/FormField'
import {ReactComponent as DeleteIcon} from '../../../../common/assets/delete-fill-icon.svg'
import {ReactComponent as DragIcon} from '../../../../common/assets/drag-icon.svg'
import QuickConfirmDialog from '../../../../components/QuickConfirmDialog'
import {useXpertKnowledgeTutorialContext} from '../../XpertKnowledgeTutorialContext'
import FormInput from '../../../../components/FormInput'
import {useSortable} from '@dnd-kit/sortable'
import {Tooltip} from '@mui/material'
import {BlobType} from '../../../../services/Blob/type'
import CustomCheckbox from '../../../../components/CustomCheckbox'
import i18n from '../../../../common/i18n'
import {useXpertKnowledgeContext} from '../../../XpertKnowledge/XpertKnowledgeContext'
import XpertKnowledgeEditSubclipModal from './XpertKnowledgeEditSubclipModal'
import Loader from '../../../../components/Loader'

interface XpertKnowledgeTutorialEditSubSectionProps {
  xpertKnowledgeSubSection: XpertKnowledgeSubSectionDto
  xpertKnowledgeSectionId: number
  index: number
  onSubSectionChange: (updatedSubSection: XpertKnowledgeSubSectionDto) => void
  onDeleteSubSection: (subSectionId: number) => void
  onOpenDeleteConfirm: (value: boolean) => void
}

const XpertKnowledgeTutorialEditSubSection: React.FC<XpertKnowledgeTutorialEditSubSectionProps> = ({
  xpertKnowledgeSubSection,
  xpertKnowledgeSectionId,
  index,
  onSubSectionChange,
  onDeleteSubSection,
  onOpenDeleteConfirm,
}) => {
  const [openEditSubclipModal, setOpenEditSubclipModal] = useState(false)
  const [recordingUrl, setRecordingUrl] = useState<string | undefined>(undefined)
  const [startTimestamp, setStartTimestamp] = useState(xpertKnowledgeSubSection.startTimestamp)
  const [endTimestamp, setEndTimestamp] = useState(xpertKnowledgeSubSection.endTimestamp)
  const [isUpdatingSubclip, setIsUpdatingSubclip] = useState(false)
  const [isLoadingAssets, setIsLoadingAssets] = useState(false)

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
  const {fetchXpertKnowledgeAssets, selectedXpertKnowledge} = useXpertKnowledgeContext()

  const {deleteXpertKnowledgeTutorialSubSection, updateXpertKnowledgeSubclip} = useXpertKnowledgeTutorialContext()
  const [isHovered, setIsHovered] = useState(false)

  const draggable = useSortable({
    id: xpertKnowledgeSubSection.id,
    data: {xpertKnowledgeSubSection, index},
  })

  const style = {
    transform: CSS.Translate.toString(draggable.transform),
    transition: draggable.transition || 'transform 0.2s ease',
    opacity: draggable.isDragging ? 0 : 1,
    zIndex: draggable.isDragging ? 999 : 'auto',
  }

  const handleMediaVisibilityChange = (mediaIndex: number, checked: boolean) => {
    const selectedMedia = xpertKnowledgeSubSection.medias?.[mediaIndex]

    if (selectedMedia?.blob.type === BlobType.IMAGE) {
      const updatedMedias = xpertKnowledgeSubSection.medias?.map((media, idx) => ({
        ...media,
        isShow: media.blob.type === BlobType.IMAGE ? idx === mediaIndex && checked : media.isShow,
      }))
      onSubSectionChange({...xpertKnowledgeSubSection, medias: updatedMedias})
      return
    }

    const updatedMedias = xpertKnowledgeSubSection.medias?.map((media, idx) => (idx === mediaIndex ? {...media, isShow: checked} : media))
    onSubSectionChange({...xpertKnowledgeSubSection, medias: updatedMedias})
  }

  const handleSaveSubclip = async () => {
    try {
      setIsUpdatingSubclip(true)
      await updateXpertKnowledgeSubclip(xpertKnowledgeSectionId, xpertKnowledgeSubSection.id, {
        startTimestamp: startTimestamp,
        endTimestamp: endTimestamp,
      })
      setOpenEditSubclipModal(false)
      onOpenDeleteConfirm(false)
    } catch (error) {
      console.error('Failed to update subclip:', error)
    } finally {
      setIsUpdatingSubclip(false)
    }
  }

  const handleEditSubclipClick = async () => {
    if (!selectedXpertKnowledge) return

    try {
      setIsLoadingAssets(true)
      const assetData = await fetchXpertKnowledgeAssets(selectedXpertKnowledge.id)
      const recUrl = assetData.recordingBlob?.url
      const events = assetData.mergedJsonBlob?.events

      if (!recUrl || !events) {
        console.error('Failed to load assets.')
      } else {
        setRecordingUrl(recUrl)
        setOpenEditSubclipModal(true)
        onOpenDeleteConfirm(true)
      }
    } catch (error) {
      console.error('Failed to fetch assets:', error)
    } finally {
      setIsLoadingAssets(false)
    }
  }

  return (
    <Col gap="10px" backgroundColor="#EDEEEF" padding="24px 28px" borderRadius="6px" style={style} props={{ref: draggable.setNodeRef}}>
      <Row justifyContent="space-between" alignItems="center">
        <Row alignItems="center" gap="10px">
          <DragIcon width={24} height={24} color="#888" style={{cursor: 'grab'}} {...draggable.attributes} {...draggable.listeners} />
          <Block fontWeight={500} lineHeight="36px" fontSize="24px">
            {i18n.t('xk.tutorial.edit.subSections.subSection', {number: index + 1})}
          </Block>
        </Row>
        <Block
          props={{
            onClick: () => {
              setOpenDeleteConfirm(true)
              onOpenDeleteConfirm(true)
            },
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          }}
          style={{cursor: 'pointer'}}
        >
          <Tooltip title={i18n.t('xk.tutorial.edit.subSections.deleteSubSectionTooltip')} arrow>
            <DeleteIcon width={28} height={28} color={isHovered ? '#B01A27' : '#C71E2C'} />
          </Tooltip>
        </Block>
      </Row>
      <Col gap="8px">
        <Block fontSize="14px" lineHeight="24px" fontWeight={500}>
          {i18n.t('xk.tutorial.edit.subSections.title')}
        </Block>
        <DynamicFormField
          placeholder={i18n.t('xk.tutorial.edit.subSections.titlePlaceholder')}
          value={xpertKnowledgeSubSection.title || ''}
          onEdit={(value) => onSubSectionChange({...xpertKnowledgeSubSection, title: value})}
        />
      </Col>
      <Col gap="8px">
        <Block fontSize="14px" lineHeight="24px" fontWeight={500}>
          {i18n.t('xk.tutorial.edit.subSections.content')}
        </Block>
        <FormInput
          placeholder={i18n.t('xk.tutorial.edit.subSections.contentPlaceholder')}
          value={xpertKnowledgeSubSection.content || ''}
          onSubmit={(value) => onSubSectionChange({...xpertKnowledgeSubSection, content: value})}
          debounce={0}
          richTextEnabled={true}
        />
      </Col>
      {xpertKnowledgeSubSection.medias?.length > 0 && (
        <Col gap="10px">
          <Row justifyContent="space-between" alignItems="center" width="100%">
            <Block fontWeight={500} fontSize="12px">
              {i18n.t('xk.tutorial.edit.subSections.availableMedia')}
            </Block>
            {xpertKnowledgeSubSection.medias?.find((media) => media.blob.type === BlobType.VIDEO) && (
              <Block
                component="button"
                padding="6px 12px"
                fontSize="12px"
                fontWeight={500}
                backgroundColor="#F4F4F5"
                border="1px solid #D1D5DB"
                borderRadius="6px"
                cursor={isLoadingAssets ? 'not-allowed' : 'pointer'}
                hoverBackgroundColor={isLoadingAssets ? '#F4F4F5' : '#E5E7EB'}
                borderColor="#9CA3AF"
                opacity={isLoadingAssets ? 0.6 : 1}
                props={{
                  onClick: handleEditSubclipClick,
                  disabled: isLoadingAssets,
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {isLoadingAssets && <Loader size={14} borderWidth="2px" />}
                {i18n.t('xk.tutorial.edit.subSections.editSubclip')}
              </Block>
            )}
          </Row>
          <Row width="100%" gap="10px" flexWrap="wrap">
            {xpertKnowledgeSubSection.medias?.map((media, mediaIndex) => (
              <Col key={media.blob.url} marginBottom="10px" width="calc(30% - 2px)" gap="10px" className="ph-no-capture">
                <Row gap="10px" alignItems="center">
                  <CustomCheckbox checked={media.isShow} onChange={(checked) => handleMediaVisibilityChange(mediaIndex, checked)} />
                  <Block fontWeight={500} fontSize="12px">
                    {mediaIndex}
                  </Block>
                </Row>
                {media.blob.type === BlobType.IMAGE && (
                  <img src={media.blob.url} alt="Sub-section media" style={{width: '100%', borderRadius: '4px'}} />
                )}
                {media.blob.type === BlobType.VIDEO && (
                  <video controls style={{width: '100%', borderRadius: '4px'}}>
                    <source src={media.blob.url} type="video/mp4" />
                  </video>
                )}
              </Col>
            ))}
          </Row>
        </Col>
      )}
      <QuickConfirmDialog
        key={xpertKnowledgeSubSection.id}
        open={openDeleteConfirm}
        setOpen={() => {
          setOpenDeleteConfirm(false)
          onOpenDeleteConfirm(false)
        }}
        title={i18n.t('xk.tutorial.edit.subSections.quickDelete.confirmTitle')}
        content={i18n.t('xk.tutorial.edit.subSections.quickDelete.confirmContent')}
        buttons={[
          {
            title: i18n.t('xk.tutorial.edit.subSections.quickDelete.buttonDelete'),
            onClick: () => {
              if ((xpertKnowledgeSubSection as any).isNew) onDeleteSubSection(xpertKnowledgeSubSection.id)
              else deleteXpertKnowledgeTutorialSubSection(xpertKnowledgeSectionId, xpertKnowledgeSubSection.id)
            },
            closeOnClick: true,
            customButtonProps: {
              backgroundColor: '#F1164A',
              hoverBackgroundColor: '#C41E2C',
              color: '#FFFFFF',
            },
          },
        ]}
      />
      <QuickConfirmDialog
        key={xpertKnowledgeSubSection.id}
        maxWidth="790px"
        open={openEditSubclipModal}
        setOpen={() => {
          if (!isUpdatingSubclip) {
            setOpenEditSubclipModal(false)
            onOpenDeleteConfirm(false)
          }
        }}
        title={i18n.t('xk.tutorial.edit.subSections.editSubclip')}
        content={
          <Col justifyContent="center" alignItems="center" width="100%">
            {isUpdatingSubclip ? (
              <Col gap="20px" alignItems="center" padding="40px">
                <Loader size={32} borderWidth="4px" />
                <Block fontSize="14px" color="#666" textAlign="center">
                  {i18n.t('xk.tutorial.edit.subSections.updatingSubclip')}
                </Block>
              </Col>
            ) : (
              <XpertKnowledgeEditSubclipModal
                fullrecordingUrl={recordingUrl}
                startTimestamp={xpertKnowledgeSubSection.medias.find((m) => m.blob.type == BlobType.VIDEO)?.startTimestamp}
                endTimestramp={xpertKnowledgeSubSection.medias.find((m) => m.blob.type == BlobType.VIDEO)?.endTimestamp}
                onStartTimestampChange={setStartTimestamp}
                onEndTimestampChange={setEndTimestamp}
              />
            )}
          </Col>
        }
        buttons={[
          {
            title: i18n.t('xk.tutorial.edit.subSections.save'),
            onClick: handleSaveSubclip,
            closeOnClick: false,
          },
        ]}
      />
    </Col>
  )
}

export default XpertKnowledgeTutorialEditSubSection
